// Browser side of alerts: the service worker (public/sw.js) and the push subscription.
import { ROUTES, type PushResult, type PushStatus } from '@serb/shared';
import { api } from '../../api/client';
import { deviceLabel } from '../../lib/device';

export const pushSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

let registration: Promise<ServiceWorkerRegistration> | null = null;
export function serviceWorker(): Promise<ServiceWorkerRegistration> {
  registration ??= navigator.serviceWorker.register('/sw.js').then(() => navigator.serviceWorker.ready);
  return registration;
}

const currentSubscription = async () => (await serviceWorker()).pushManager.getSubscription();

function base64UrlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const raw = atob((s + '='.repeat((4 - (s.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** Is this device subscribed, and which devices get alerts */
export async function alertStatus(): Promise<PushStatus & { permission: NotificationPermission }> {
  const sub = await currentSubscription();
  const status = await api.get<PushStatus>(ROUTES.api.pushStatus, { endpoint: sub?.endpoint ?? '' });
  return { ...status, on: Boolean(sub) && status.on, permission: Notification.permission };
}

/** Must be called straight from a tap (iPhone rule). Returns false if he did not allow alerts. */
export async function turnOnAlerts(): Promise<boolean> {
  if ((await Notification.requestPermission()) !== 'granted') return false;
  const reg = await serviceWorker();
  const { publicKey } = await api.get<{ publicKey: string }>(ROUTES.api.pushKey);
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(publicKey) }));
  await api.post(ROUTES.api.pushSubscribe, { subscription: sub.toJSON(), label: deviceLabel });
  return true;
}

export async function turnOffAlerts(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await api.post(ROUTES.api.pushUnsubscribe, { endpoint: sub.endpoint });
  await sub.unsubscribe().catch(() => {});
}

export const sendTestAlert = () => api.post<PushResult>(ROUTES.api.pushTest);
